import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Minus, Trash2, Send, Link2, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCreateOrder = ({ adminToken, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    delivery_address: '',
    delivery_type: 'delivery',
    delivery_charge: 0,
    scheduled_date: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const addProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const existing = selectedItems.find(i => i.product_id === productId);
    if (existing) {
      setSelectedItems(selectedItems.map(i =>
        i.product_id === productId ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setSelectedItems([...selectedItems, {
        product_id: productId,
        product: product,
        quantity: 1
      }]);
    }
  };

  const updateQuantity = (productId, delta) => {
    setSelectedItems(selectedItems.map(i => {
      if (i.product_id === productId) {
        const newQty = i.quantity + delta;
        return newQty > 0 ? { ...i, quantity: newQty } : i;
      }
      return i;
    }).filter(i => i.quantity > 0));
  };

  const removeItem = (productId) => {
    setSelectedItems(selectedItems.filter(i => i.product_id !== productId));
  };

  const calculateSubtotal = () => {
    return selectedItems.reduce((sum, i) => sum + (i.product.price * i.quantity), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + Number(formData.delivery_charge || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Please enter customer name and phone');
      return;
    }
    
    if (selectedItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    if (formData.delivery_type === 'delivery' && !formData.delivery_address) {
      toast.error('Please enter delivery address');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        ...formData,
        delivery_charge: Number(formData.delivery_charge || 0),
        items: selectedItems.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity
        }))
      };

      const response = await axios.post(`${API}/admin/orders/create`, orderData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      setResult(response.data);
      toast.success('Order created successfully!');
      onSuccess?.();
      
      // Reset form
      setSelectedItems([]);
      setFormData({
        customer_name: '',
        customer_phone: '',
        delivery_address: '',
        delivery_type: 'delivery',
        delivery_charge: 0,
        scheduled_date: '',
        notes: ''
      });
    } catch (error) {
      toast.error('Failed to create order');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyPaymentLink = () => {
    if (result?.payment_link) {
      navigator.clipboard.writeText(result.payment_link);
      toast.success('Payment link copied!');
    }
  };

  // Group products by category
  const productsByCategory = products.reduce((acc, product) => {
    const cat = product.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Product Selection */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold text-accent mb-4">Select Products</h3>
        
        <div className="space-y-4 max-h-[500px] overflow-y-auto">
          {Object.entries(productsByCategory).map(([category, categoryProducts]) => (
            <div key={category}>
              <h4 className="font-medium text-sm text-muted-foreground uppercase mb-2">{category}</h4>
              <div className="space-y-2">
                {categoryProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between p-2 rounded-lg border hover:bg-gray-50 cursor-pointer gap-2"
                    onClick={() => addProduct(product.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.weight}</p>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                      <span className="font-bold text-primary text-sm">₹{product.price}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7 sm:h-8 sm:w-8 bg-primary/10 border-primary/20">
                        <Plus className="h-4 w-4 text-primary" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Order Form */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-lg font-bold text-accent mb-4">Order Details</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Selected Items */}
          {selectedItems.length > 0 && (
            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <p className="font-medium text-sm">Selected Items:</p>
              {selectedItems.map((item) => (
                <div key={item.product_id} className="flex items-center justify-between">
                  <span className="text-sm">{item.product.name}</span>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-6 w-6"
                      onClick={() => updateQuantity(item.product_id, -1)}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-6 w-6"
                      onClick={() => updateQuantity(item.product_id, 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-red-500"
                      onClick={() => removeItem(item.product_id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                    <span className="w-16 text-right font-medium">₹{item.product.price * item.quantity}</span>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Items Subtotal:</span>
                  <span>₹{calculateSubtotal()}</span>
                </div>
                {Number(formData.delivery_charge) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Delivery Charge:</span>
                    <span>₹{formData.delivery_charge}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold pt-1 border-t">
                  <span>Total:</span>
                  <span className="text-primary">₹{calculateTotal()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Customer Name *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="Customer name"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                placeholder="+91..."
              />
            </div>
          </div>

          <div>
            <Label>Delivery Type</Label>
            <Select
              value={formData.delivery_type}
              onValueChange={(value) => setFormData({ ...formData, delivery_type: value, delivery_charge: value === 'pickup' ? 0 : formData.delivery_charge })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delivery">Home Delivery</SelectItem>
                <SelectItem value="pickup">Store Pickup</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.delivery_type === 'delivery' && (
            <>
              <div>
                <Label>Delivery Address *</Label>
                <Textarea
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                  placeholder="Full delivery address"
                  rows={2}
                />
              </div>
              <div>
                <Label>Delivery Charge (₹)</Label>
                <Input
                  type="number"
                  value={formData.delivery_charge}
                  onChange={(e) => setFormData({ ...formData, delivery_charge: e.target.value })}
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-muted-foreground mt-1">Enter delivery charge manually</p>
              </div>
            </>
          )}

          <div>
            <Label>Scheduled Date (Optional)</Label>
            <Input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
            />
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Special instructions..."
              rows={2}
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12"
            disabled={loading || selectedItems.length === 0}
          >
            {loading ? 'Creating...' : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Create Order & Generate Payment Link
              </>
            )}
          </Button>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-bold text-green-800 mb-2">Order Created!</h4>
            <p className="text-sm text-green-700 mb-2">Order ID: {result.order_id?.slice(0, 8).toUpperCase()}</p>
            <p className="text-sm text-green-700 mb-3">Total: ₹{result.total}</p>
            
            {result.payment_link && (
              <div className="flex items-center gap-2">
                <Input
                  value={result.payment_link}
                  readOnly
                  className="flex-1 text-sm"
                />
                <Button size="icon" variant="outline" onClick={copyPaymentLink}>
                  <Copy className="h-4 w-4" />
                </Button>
                <a href={result.payment_link} target="_blank" rel="noopener noreferrer">
                  <Button size="icon" variant="outline">
                    <Link2 className="h-4 w-4" />
                  </Button>
                </a>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminCreateOrder;
